// What the parser needs of a repeat rule: the first day a new series falls on (Models.swift: RepeatRule.firstDay).
// Kept here until the core has its own repeats module; then this should move there.

import type { Day, MonthPosition, RepeatRule } from "../types";
import { addDays, addMonths, daysInMonth, startOfMonth, weekdayOf, yearOf } from "./calendar";

/** The weekdays the rule falls on, or undefined when it doesn't use any (only weekly rules do). */
function activeWeekdays(rule: RepeatRule): RepeatRule["weekdays"] {
  return rule.unit === "week" && rule.weekdays && rule.weekdays.length > 0 ? rule.weekdays : undefined;
}

/** The place in the month, or undefined when the rule repeats on the day number (only monthly rules use one). */
function activeMonthPosition(rule: RepeatRule): MonthPosition | undefined {
  return rule.unit === "month" ? rule.monthPosition : undefined;
}

/** The day `position` falls on in the month that starts at `month`, or undefined if there is none. */
function dayInMonth(position: MonthPosition, month: Day): Day | undefined {
  const length = daysInMonth(yearOf(month), Number(month.slice(5, 7)));
  if (position.weekday === undefined) {
    // A day of the month exists only as the first or the last.
    if (position.ordinal === -1) return addDays(month, length - 1);
    return position.ordinal === 1 ? month : undefined;
  }
  if (position.ordinal === -1) {
    const last = addDays(month, length - 1);
    const back = (weekdayOf(last) - position.weekday + 7) % 7;
    return addDays(last, -back);
  }
  if (position.ordinal < 1 || position.ordinal > 4) return undefined;
  const ahead = (position.weekday - weekdayOf(month) + 7) % 7;
  return addDays(month, ahead + (position.ordinal - 1) * 7);
}

/** The chosen days of the month, or undefined when it uses none (only monthly rules without a place in the month). */
function activeMonthDays(rule: RepeatRule): number[] | undefined {
  if (rule.unit !== "month" || rule.monthPosition !== undefined || rule.monthDays === undefined) return undefined;
  const days = [...new Set(rule.monthDays.filter((d) => Number.isInteger(d) && d >= 1 && d <= 31))].sort((a, b) => a - b);
  return days.length > 0 ? days : undefined;
}

/** The first day on or after `day` that the rule falls on. */
export function firstDay(rule: RepeatRule, day: Day): Day {
  const monthDays = activeMonthDays(rule);
  if (monthDays) {
    // A day past the month's end falls on its last day.
    let month = startOfMonth(day);
    for (let i = 0; i < 12 * 50; i++) {
      const length = daysInMonth(yearOf(month), Number(month.slice(5, 7)));
      const candidate = monthDays.map((d) => addDays(month, Math.min(d, length) - 1)).sort().find((d) => d >= day);
      if (candidate !== undefined) return candidate;
      month = addMonths(month, 1);
    }
    return day;
  }
  const position = activeMonthPosition(rule);
  if (position) {
    // The kit looks fifty years ahead before it gives up; a valid position is found in the first two months.
    let month = startOfMonth(day);
    for (let i = 0; i < 12 * 50; i++) {
      const candidate = dayInMonth(position, month);
      if (candidate !== undefined && candidate >= day) return candidate;
      month = addMonths(month, 1);
    }
    return day;
  }
  const weekdays = activeWeekdays(rule);
  if (!weekdays) return day;
  let candidate = day;
  for (let i = 0; i < 7; i++) {
    if (weekdays.includes(weekdayOf(candidate))) return candidate;
    candidate = addDays(candidate, 1);
  }
  return day;
}
